// @ts-nocheck
import _ from 'lodash';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles, Box } from '@material-ui/core';
import {
  useHistoryState,
  useOnChange,
  useAlert,
  useOnMount,
  useIfMounted,
} from '../../../../../common/hooks';
import { AppRoute } from '../../../../app';
import { EmptyState, LoadingWrapper, Panel } from '../../../../../common/components';
import { AsyncUtils } from '../../../../../common/utils';
import { AlertSeverity } from '../../../../../common/providers';
import { selectPendingActions } from '../../../../../common/state/pending';
import ProductList from './partials/ProductList';
import { selectProduct, fetchProducts, fetchProductChange } from '../../../state/productActions';
import { selectProducts, selectProductChanges } from '../../../state/productSelectors';

const useStyles = makeStyles(() => ({
  root: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
  },
  panel: {
    flex: '1',
    height: '100%',
    paddingTop: '68px',
    paddingLeft: '0',
    paddingRight: '0',
    paddingBottom: '0',
  },
}));

function ProductsPage() {
  const [loading, setLoading] = useHistoryState('loading', true);
  const [t] = useTranslation();
  const { showAlert } = useAlert();
  const products = useSelector(selectProducts);
  const productChanges = useSelector(selectProductChanges);
  const pendingActions = useSelector(selectPendingActions);
  const history = useHistory();
  const classes = useStyles();
  const ifMounted = useIfMounted();
  const dispatch = useDispatch();

  const [filterParams, setFilterParams] = useHistoryState('filterParams', {
    onlyBuyable: true,
    channels: false,
    clientSegment: '',
  });

  useOnMount(() => {
    async function handleFetchData() {
      try {
        if (!products) {
          setLoading(true);
        }
        await AsyncUtils.executeAll([
          dispatch(fetchProductChange()),
          dispatch(fetchProducts(filterParams)),
        ]);
      } catch (e) {
        showAlert({
          title: t('product.productsFetchError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
      } finally {
        ifMounted(() => setLoading(false));
      }
    }

    handleFetchData();
  });

  const handleFetchProducts = useCallback(
    async () => {
      try {
        await dispatch(fetchProducts(filterParams));
      } catch (e) {
        showAlert({
          title: t('product.productsFetchError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
      }
    },
    [dispatch, filterParams, showAlert, t]
  );

  const handleFilterChange = useCallback(
    params => {
      setFilterParams({
        ...filterParams,
        ...params,
      });
    },
    [filterParams, setFilterParams]
  );

  const handleSelectProduct = useCallback(
    async params => {
      history.push(AppRoute.ProductDetails.replace(':productId', params.row.id), {
        from: history.location,
      });
    },
    [history]
  );

  useOnChange(filterParams, () => {
    handleFetchProducts();
  });

  return (
    <Box id="Products-container" className={classes.root}>
      <LoadingWrapper loading={loading}>
        <Panel id="Products-panel" title={t('product.products')} className={classes.panel}>
          {!products ? (
            <EmptyState variant="unavailable" message={t('common.empty')} />
          ) : (
            <ProductList
              products={products.products}
              productChanges={productChanges ?? {}}
              filterParams={filterParams}
              onSelectProduct={handleSelectProduct}
              onFilterChange={handleFilterChange}
              actionsDisabled={!_.isEmpty(pendingActions)}
            />
          )}
        </Panel>
      </LoadingWrapper>
    </Box>
  );
}

export default ProductsPage;
